import { useEffect } from 'react';
import { create } from 'zustand';
import type { User, Session } from '@supabase/supabase-js';

import { supabase } from '@/db/client';

import { authService } from '../services/authService';
import type { Profile, AuthState } from '../auth.types';

interface AuthStore extends AuthState {
  setSession: (session: Session | null, user: User | null) => void;
  setProfile: (profile: Profile | null) => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  session: null,
  profile: null,
  loading: true,
  setSession: (session, user) => set({ session, user }),
  setProfile: (profile) => set({ profile }),
  setLoading: (loading) => set({ loading }),
}));

export function useAuthBootstrap(): void {
  const { setSession, setProfile, setLoading } = useAuthStore();

  useEffect(() => {
    let mounted = true;

    void (async () => {
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;

      const session = data.session;
      setSession(session, session?.user ?? null);

      if (session?.user) {
        const profile = await authService.getProfile(session.user.id);
        if (!mounted) return;
        setProfile(profile);
        void authService.updateLastSignedIn(session.user.id);
      }
      setLoading(false);
    })();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      setSession(session, session?.user ?? null);
      if (session?.user) {
        void (async () => {
          const profile = await authService.getProfile(session.user.id);
          if (!mounted) return;
          setProfile(profile);
        })();
      } else {
        setProfile(null);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [setLoading, setProfile, setSession]);
}

export interface AuthActions {
  signInWithMagicLink: (args: { email: string; redirectTo: string }) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

export function useAuth(): AuthState & AuthActions {
  const store = useAuthStore();

  const signInWithMagicLink = (args: { email: string; redirectTo: string }) =>
    authService.signInWithMagicLink(args);

  const signInWithGoogle = () => authService.signInWithGoogle();

  const signOut = async () => {
    await authService.signOut();
    store.setSession(null, null);
    store.setProfile(null);
  };

  const refreshProfile = async () => {
    if (!store.user) return;
    const profile = await authService.getProfile(store.user.id);
    store.setProfile(profile);
  };

  return { ...store, signInWithMagicLink, signInWithGoogle, signOut, refreshProfile };
}
